<template>
    <div class="code-preview">
        <iframe
            ref="previewFrame"
            :src="blobUrl"
            sandbox="allow-scripts"
            class="code-preview__frame"
        ></iframe>
    </div>
</template>

<script setup lang="ts">
import { useStekManager } from "@/composables/stek-manager";
import { useColorExtraction } from "@/composables/useColorExtraction";
import { onBeforeUnmount, onMounted, ref, watch } from "vue";

interface Props {
    code: string;
    framework?: "react" | "plain";
}

const props = withDefaults(defineProps<Props>(), {
    framework: "react",
});

const { logo, stek } = useStekManager();
const {
    primaryColor,
    secondaryColor,
    primaryTextColor,
    secondaryTextColor,
    isDarkMode,
    extractColorsFromImage,
    resetColors,
} = useColorExtraction();

const previewFrame = ref<HTMLIFrameElement | null>(null);
const blobUrl = ref("");
const logoDataUrl = ref("");

// Convert logo blob to base64 data URL and extract colors
const updateLogoDataUrl = async () => {
    if (logo.value?.blob.size) {
        // Use FileReader to convert blob to base64 data URL
        const reader = new FileReader();
        reader.onload = async (e) => {
            const dataUrl = e.target?.result as string;
            logoDataUrl.value = dataUrl;

            // Extract colors from the logo using our composable
            await extractColorsFromImage(dataUrl);

            // Rerender the preview with the new logo data URL and colors
            updatePreview(props.code);
        };
        reader.readAsDataURL(logo.value.blob);
    } else {
        logoDataUrl.value = "";
        // Reset to default Material UI colors
        resetColors();
        // Rerender the preview when logo is removed
        updatePreview(props.code);
    }
};

/* eslint-disable  no-useless-escape */
const wrapReactCode = (componentCode: string): string => {
    // Get the logo HTML
    const logoHtml = logoDataUrl.value
        ? `<img src="${logoDataUrl.value}" alt="${stek.value?.name || ""} logo" />`
        : "";

    return `<!DOCTYPE html>
<html>
<head>
    <script>
        function showError() {
            window.addEventListener('DOMContentLoaded', function() {
                document.getElementById('script-error').style.display = 'block';
                document.getElementById('script-loader').style.display = 'none';
            });
        }
    <\/script>
    <style>
        /* Top bar styles */
        .top-bar {
            display: flex;
            align-items: center;
            padding: 8px 16px;
            border-bottom: 1px solid ${primaryColor.value};
            height: 48px;
            color: #333333;
        }

        .top-bar img {
            height: 32px;
            max-width: 200px;
            object-fit: contain;
        }
        .top-bar .stek-name {
            font-size: 20px;
            margin-left: 8px;
        }

        /* Content container styles */
        #root { padding-top: 12px; }

        /* Error */
        #script-error {
            color: #721c24;
            background-color: #f8d7da;
            padding: 12px;
            margin: 10px 0;
            border-radius: 4px;
            border: 1px solid #f5c6cb;
        }

        /* Loader */
        #script-loader {
            color: #004085;
            background-color: #cce5ff;
            padding: 12px;
            margin: 10px 0;
            border-radius: 4px;
            border: 1px solid #b8daff;
        }
    </style>
    <link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Roboto:300,400,500,700&display=swap" />
    <link rel="stylesheet" href="https://fonts.googleapis.com/icon?family=Material+Icons" />
    <style>
        body {
            font-family: "Roboto", sans-serif;
            padding: 20px;
            margin: 0;
        }
    </style>
</head>
<body>
    <!-- Show loader before scripts -->
    <div id="script-loader">Loading...</div>
    <script src="https://cdn.jsdelivr.net/npm/react@18/umd/react.development.min.js"
        onerror="showError();"><\/script>
    <script src="https://cdn.jsdelivr.net/npm/react-dom@18/umd/react-dom.development.min.js"
        onerror="showError();"><\/script>
    <script src="https://cdn.jsdelivr.net/npm/@babel/standalone/babel.min.js"
        onerror="showError();"><\/script>
    <script src="https://cdn.jsdelivr.net/npm/@mui/material@5/umd/material-ui.development.min.js"
        onerror="showError();"><\/script>
    <div class="top-bar">
        ${logoHtml}
        <span class="stek-name">${stek.value?.name}</span>
    </div>
    <div id="script-error" style="display: none;">
        Failed to load some required scripts. The preview can't render. Please try again later.
    </div>
    <div id="root"></div>
    <script type="text/babel">
        // Create theme with colors extracted from logo
        const theme = MaterialUI.createTheme({
            palette: {
                primary: {
                    main: "${primaryColor.value}",
                    contrastText: "${primaryTextColor.value}",
                },
                secondary: {
                    main: "${secondaryColor.value}",
                    contrastText: "${secondaryTextColor.value}",
                },
                text: {
                    primary: "#333333", // Standard text color for content
                    secondary: "#666666" // Secondary text color
                },
                // Set the mode based on the primary color brightness
                // mode: "${isDarkMode.value ? "dark" : "light"}",
                mode: "light",
            },
        });

        // Wrap the app in ThemeProvider to apply the theme
        ${componentCode}

        const AppWithTheme = () => (
            <MaterialUI.ThemeProvider theme={theme}>
                <App />
            </MaterialUI.ThemeProvider>
        );

        const root = ReactDOM.createRoot(document.getElementById('root'));
        root.render(<AppWithTheme />);
        document.getElementById('script-loader').style.display = 'none';
    <\/script>
</body>
</html>`;
};
/* eslint-enable */

const wrapPlainCode = (plainCode: string): string => {
    // Get the logo HTML
    const logoHtml = logoDataUrl.value
        ? `<img src="${logoDataUrl.value}" alt="${stek.value?.name || ""} logo" />`
        : `<span>${stek.value?.name || "Stek"}</span>`;

    return `<!DOCTYPE html>
<html>
<head>
    <style>
        /* Top bar styles */
        .top-bar {
            display: flex;
            align-items: center;
            padding: 8px 16px;
            background-color: ${primaryColor.value};
            border-bottom: 1px solid rgba(0,0,0,0.1);
            height: 48px;
            color: ${primaryTextColor.value};
        }

        .top-bar img {
            height: 32px;
            max-width: 200px;
            object-fit: contain;
        }

        body {
            margin: 0;
            padding: 0;
            font-family: Arial, sans-serif;
        }

        .content {
            padding: 16px;
        }

        /* Use the extracted primary color for various elements */
        a {
            color: ${primaryColor.value};
        }
        button {
            background-color: ${primaryColor.value};
            color: ${primaryTextColor.value};
            border: none;
            padding: 8px 16px;
            border-radius: 4px;
            cursor: pointer;
        }

        /* Use secondary color for some elements */
        .secondary-btn {
            background-color: ${secondaryColor.value};
            color: ${secondaryTextColor.value};
        }
    </style>
</head>
<body>
    <div class="top-bar">
        ${logoHtml}
    </div>
    <div class="content">
        ${plainCode}
    </div>
</body>
</html>`;
};

const updatePreview = (code: string) => {
    // Clean up previous blob URL if it exists
    if (blobUrl.value) {
        URL.revokeObjectURL(blobUrl.value);
    }

    // Wrap the code with appropriate HTML based on the framework
    let finalCode;
    if (props.framework === "react") {
        finalCode = wrapReactCode(code);
    } else if (props.framework === "plain") {
        finalCode = wrapPlainCode(code);
    } else {
        finalCode = code;
    }

    // Create new blob URL from code
    const blob = new Blob([finalCode], { type: "text/html" });
    blobUrl.value = URL.createObjectURL(blob);
};

// Update preview when code prop changes
watch(
    () => props.code,
    (newCode) => {
        updatePreview(newCode);
    }
);

// Initial setup
onMounted(() => {
    updateLogoDataUrl();
    updatePreview(props.code);
});

// Watch for logo changes to update the logo URL
watch(
    () => logo.value?.blob,
    () => {
        updateLogoDataUrl();
    }
);

// Cleanup
onBeforeUnmount(() => {
    if (blobUrl.value) {
        URL.revokeObjectURL(blobUrl.value);
    }
});
</script>

<style scoped>
.code-preview {
    width: 100%;
    height: 750px;
    border: 1px solid var(--border-color, #ddd);
    border-radius: 4px;
    overflow: hidden;
}

.code-preview__frame {
    width: 100%;
    height: 100%;
    border: none;
    background: white;
}
</style>
